(function () {
  'use strict';
  const storageKey = 'dirt-mix-drafts-v1';
  const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const count = draft => Object.values(draft).reduce((total, quantity) => total + quantity, 0);
  const target = quantity => Math.max(6, Math.ceil(quantity / 6) * 6);
  const validTotal = draft => Number.isSafeInteger(target(count(draft)));
  const validId = value => typeof value === 'string' && /^[1-9]\d*$/.test(value);
  /**
   * The only cart write of the box: set this farmer's Fles lines to the box (absolute, so a repeat is harmless)
   * and leave every other line alone: other farmers, Doos variants, a DIRT.BOX or any subscription line.
   * A cart permalink (/cart/v:q) would replace the whole cart and drop the earlier boxes (17 Oct 2026).
   * items: cart.js items; variants: Set of this farmer's Fles variant ids; lines: [{variantId, quantity}].
   */
  function boxUpdates(items, variants, lines) {
    const want = new Map(lines.map(line => [line.variantId, line.quantity]));
    const updates = {};
    for (const item of Array.isArray(items) ? items : []) {
      const id = String(item?.variant_id);
      if (!variants.has(id) || item.selling_plan_allocation || typeof item.key !== 'string') continue;
      updates[item.key] = want.get(id) ?? 0;
      want.set(id, 0);
    }
    for (const [id, quantity] of want) if (quantity) updates[id] = quantity;
    return updates;
  }

  /**
   * catalog: {currency:'EUR', wines:[{handle,vendor,variantId:string,price:integer,available:boolean,name?}], farmers:[{name}], excluded?:[{handle,vendor,name}]}.
   * Liquid supplies current commerce values for the explicit 119-product mix allowlist, never the wider catalog.
   * storage/eventTarget/fetch/location are browser boundaries injectable for framework-free tests.
   */
  function createMixAdapter({ catalog, storage, eventTarget, fetch: fetchProduct = globalThis.fetch, location = 'https://dirt.family/', root = '/' } = {}) {
    if (!object(catalog) || catalog.currency !== 'EUR' || !Array.isArray(catalog.wines) || !Array.isArray(catalog.farmers) || !catalog.farmers.length) throw new TypeError('Invalid mix catalog');
    const currency = catalog.currency;
    const farmers = new Set(catalog.farmers.map(item => item?.name));
    if (farmers.size !== catalog.farmers.length || [...farmers].some(name => typeof name !== 'string' || !name.trim() || ['__proto__', 'constructor', 'prototype'].includes(name))) throw new TypeError('Invalid mix catalog farmers');
    const wines = new Map();
    const variants = new Set();
    for (const wine of catalog.wines) {
      if (!object(wine) || typeof wine.handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(wine.handle) || !farmers.has(wine.vendor) || !validId(wine.variantId) || !Number.isSafeInteger(wine.price) || wine.price < 0 || typeof wine.available !== 'boolean' || wines.has(wine.handle) || variants.has(wine.variantId)) throw new TypeError('Invalid mix catalog wine');
      wines.set(wine.handle, { ...wine });
      variants.add(wine.variantId);
    }
    const excluded = new Map();
    if (catalog.excluded !== undefined && !Array.isArray(catalog.excluded)) throw new TypeError('Invalid mix catalog exclusions');
    for (const item of catalog.excluded ?? []) {
      if (!object(item) || Object.keys(item).some(key => !['handle', 'vendor', 'name'].includes(key)) || typeof item.handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(item.handle) || !farmers.has(item.vendor) || typeof item.name !== 'string' || !item.name.trim() || wines.has(item.handle) || excluded.has(item.handle)) throw new TypeError('Invalid mix catalog exclusion');
      excluded.set(item.handle, { handle: item.handle, vendor: item.vendor, name: item.name });
    }
    let farmer = null; // no farmer until the visitor picks one; never favour the first in the list
    let drafts = {};
    const livePrices = Object.fromEntries([...wines.values()].map(wine => [wine.handle, wine.price]));
    const liveCompareAtCents = Object.fromEntries([...wines.values()].map(wine => [wine.handle, Number.isSafeInteger(wine.compareAtCents) ? wine.compareAtCents : null]));
    let checking = false;
    let notice = null;
    let lastSaved;
    let request = null;
    let readyBase = null;
    const listeners = new Set();
    const selection = () => JSON.stringify([farmer, drafts[farmer] ?? {}]);
    function getState() {
      return Object.freeze({
        farmer,
        drafts: Object.freeze(Object.fromEntries(Object.entries(drafts).map(([vendor, draft]) => [vendor, Object.freeze({ ...draft })]))),
        livePrices: Object.freeze({ ...livePrices }),
        liveCompareAtCents: Object.freeze({ ...liveCompareAtCents }),
        liveAvailability: Object.freeze(Object.fromEntries([...wines.values()].map(wine => [wine.handle, wine.available]).concat([...excluded.keys()].map(handle => [handle, false])))), currency, checking,
        notice: notice && Object.freeze({ ...notice }),
      });
    }
    function emit() {
      const state = getState();
      for (const listener of listeners) listener(state);
    }
    function warn(code, message) { notice = { code, message }; }
    function restore(value) {
      const result = {};
      if (!object(value)) return result;
      for (const vendor of farmers) {
        if (!object(value[vendor])) continue;
        const draft = {};
        for (const [handle, quantity] of Object.entries(value[vendor])) {
          if ((wines.get(handle) || excluded.get(handle))?.vendor !== vendor || !Number.isSafeInteger(quantity) || quantity < 1) continue;
          if (validTotal({ ...draft, [handle]: quantity })) draft[handle] = quantity;
        }
        result[vendor] = draft;
      }
      return result;
    }
    function sync() {
      if (!storage) return;
      let raw;
      try { raw = storage.getItem(storageKey); }
      catch { warn('storage_unavailable', 'Je selectie blijft in dit tabblad bewaard, maar kan nu niet worden opgeslagen.'); return; }
      if (raw === lastSaved) return;
      lastSaved = raw;
      if (raw === null) {
        drafts = {};
        return;
      }
      try {
        const saved = JSON.parse(raw);
        if (!object(saved) || !object(saved.drafts)) throw new Error('Invalid draft');
        const recovered = restore(saved.drafts);
        if (JSON.stringify(recovered) !== JSON.stringify(saved.drafts) || !farmers.has(saved.farmer)) warn('storage_recovered', 'Een eerdere keuze kon niet worden hersteld. Je overige flessen zijn bewaard.');
        drafts = recovered;
        if (farmers.has(saved.farmer)) farmer = saved.farmer;
      } catch { warn('storage_corrupt', 'Je opgeslagen selectie kon niet worden gelezen. Je huidige keuze blijft bewaard.'); }
    }
    function save() {
      if (storage) {
        try {
          const raw = JSON.stringify({ drafts, farmer });
          storage.setItem(storageKey, raw);
          lastSaved = raw;
        } catch { warn('storage_unavailable', 'Je selectie blijft in dit tabblad bewaard, maar kan nu niet worden opgeslagen.'); }
      }
      emit();
    }
    function commit(wine, quantity) {
      const previous = selection();
      const before = count(drafts[wine.vendor] ?? {});
      const next = { ...(drafts[wine.vendor] ?? {}) };
      if (quantity === 0) delete next[wine.handle]; else next[wine.handle] = quantity;
      if (!validTotal(next)) return false;
      drafts = { ...drafts, [wine.vendor]: next };
      farmer = wine.vendor;
      if (selection() !== previous) readyBase = null;
      notice = null;
      // A full box never grows silently: the bottle that starts the next box says so.
      const after = count(next), box = Math.ceil(after / 6), open = 6 - after % 6;
      if (after > before && open < 6 && box > Math.max(1, Math.ceil(before / 6))) warn('box_next', `Je ${box === 2 ? 'eerste' : 'vorige'} doos van ${wine.vendor} is vol, dus je begint aan doos ${box}. Nog ${open} ${open === 1 ? 'fles' : 'flessen'}, dan is die ook vol.${box === 2 ? ' Bij 12 flessen van dezelfde boer is de bezorging gratis.' : ''}`);
      save();
      return true;
    }
    function setQuantity(handle, quantity) {
      if (checking) return false;
      sync();
      const wine = wines.get(handle) || excluded.get(handle);
      if (!wine || !Number.isSafeInteger(quantity) || quantity < 0 || (quantity > (drafts[wine.vendor]?.[handle] ?? 0) && !wine.available)) return false;
      if (excluded.has(handle) && quantity >= (drafts[wine.vendor]?.[handle] ?? 0)) return false;
      return commit(wine, quantity);
    }
    function add(handle, quantity = 1) {
      if (checking) return false;
      sync();
      const wine = wines.get(handle);
      if (!wine?.available || !Number.isSafeInteger(quantity) || quantity < 1) return false;
      const next = (drafts[wine.vendor]?.[handle] ?? 0) + quantity;
      if (!Number.isSafeInteger(next)) return false;
      return commit(wine, next);
    }
    function selectFarmer(vendor, preserveRecoveryNotice = false) {
      if (checking) return false;
      sync();
      if (!farmers.has(vendor)) return false;
      const recoveryNotice = preserveRecoveryNotice && /^storage_(corrupt|recovered)$/.test(notice?.code) ? notice : null;
      notice = null;
      if (farmer !== vendor && count(drafts[farmer] ?? {})) warn('farmer_changed', `Je doos van ${farmer} blijft bewaard. Je kiest nu bij ${vendor}.`);
      farmer = vendor;
      if (recoveryNotice) notice = recoveryNotice;
      save();
      return true;
    }
    function acceptProposal(proposal) {
      if (checking || !object(proposal)) return { status: 'invalid' };
      const { vendor, base, additions } = proposal;
      sync();
      if (!farmers.has(vendor) || !object(base) || !object(additions)) return { status: 'invalid' };
      const active = drafts[vendor] ?? {};
      if (farmer !== vendor || JSON.stringify(active) !== JSON.stringify(base)) { emit(); return { status: 'stale' }; }
      if (Object.keys(active).some(handle => excluded.has(handle))) return { status: 'invalid' };
      const lines = Object.entries(additions);
      if (!lines.length || lines.some(([handle, quantity]) => !Number.isSafeInteger(quantity) || quantity < 1 || wines.get(handle)?.vendor !== vendor || !wines.get(handle).available)) return { status: 'invalid' };
      const total = count(active) + count(additions);
      if (!Number.isSafeInteger(total) || total !== target(count(active))) return { status: 'invalid' };
      const next = { ...active };
      for (const [handle, quantity] of lines) next[handle] = (next[handle] ?? 0) + quantity;
      if (!validTotal(next)) return { status: 'invalid' };
      drafts = { ...drafts, [vendor]: next };
      farmer = vendor;
      readyBase = null;
      notice = null;
      save();
      return { status: 'applied' };
    }
    function failure(code, message, status = 'error') {
      return Object.assign(new Error(message), { code, status });
    }
    function finishFailure(error) {
      const known = typeof error?.code === 'string' && ['error', 'unavailable'].includes(error.status);
      warn(known ? error.code : 'network', known ? error.message : 'De winkel is even niet bereikbaar. Probeer het opnieuw; je selectie blijft bewaard.');
      return { status: known ? error.status : 'error', message: notice.message };
    }
    async function prepareHandoff({ signal } = {}) {
      if (checking) return { status: 'error', message: 'Je selectie wordt al gecontroleerd.' };
      sync();
      const base = selection();
      const draft = { ...(drafts[farmer] ?? {}) };
      const vendor = farmer;
      const quantity = count(draft);
      const unavailable = Object.keys(draft).map(handle => excluded.get(handle)).find(Boolean);
      if (unavailable) {
        const result = finishFailure(failure('product_excluded', `${unavailable.name} is niet beschikbaar in de winkel. Haal deze fles uit je doos en kies een andere wijn; je overige selectie blijft bewaard.`, 'unavailable'));
        emit();
        return result;
      }
      if (quantity <= 0 || !Number.isSafeInteger(quantity) || quantity % 6 !== 0) {
        const result = finishFailure(failure('incomplete', 'Kies een veelvoud van zes flessen per wijnboer. Controleer je selectie.', 'unavailable'));
        emit();
        return result;
      }
      if (base === readyBase) return { status: 'error', message: 'De link naar je winkelmand is al klaargezet.' };
      if (signal?.aborted) {
        const result = finishFailure(failure('cancelled', 'De controle is geannuleerd. Je selectie blijft bewaard.'));
        emit();
        return result;
      }
      const controller = new AbortController();
      request = { controller, base };
      checking = true;
      notice = null;
      const cancel = () => controller.abort(failure('cancelled', 'De controle is geannuleerd. Je selectie blijft bewaard.'));
      signal?.addEventListener('abort', cancel, { once: true });
      const timer = setTimeout(() => controller.abort(failure('timeout', 'De winkel is even niet bereikbaar. Probeer het opnieuw; je selectie blijft bewaard.')), 10000);
      let onAbort;
      const aborted = new Promise((_, reject) => {
        onAbort = () => reject(controller.signal.reason);
        controller.signal.addEventListener('abort', onAbort, { once: true });
      });
      emit();
      try {
        const currentUrl = new URL(typeof location === 'string' ? location : location.href);
        if (!/^\/(?:[a-zA-Z0-9-]+\/)*$/.test(root)) throw failure('route', 'De winkelroute is niet beschikbaar. Je selectie blijft bewaard.');
        const read = async path => {
          const response = await fetchProduct(new URL(`${root}${path}`, currentUrl).href, { method: 'GET', cache: 'no-store', credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw failure('network', 'De actuele voorraad is niet bereikbaar. Probeer het opnieuw; je selectie blijft bewaard.');
          return response.json();
        };
        const write = async (path, body) => {
          const response = await fetchProduct(new URL(`${root}${path}`, currentUrl).href, { method: 'POST', cache: 'no-store', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body), signal: controller.signal });
          if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw failure('cart_rejected', typeof detail?.description === 'string' && detail.description ? detail.description : 'Je doos kon niet in de winkelmand worden gezet. Probeer het opnieuw; je selectie blijft bewaard.');
          }
          return response.json();
        };
        const [cart, checked] = await Promise.race([
          Promise.all([
            read('cart.js'),
            Promise.all(Object.entries(draft).map(async ([handle, amount]) => {
              const wine = wines.get(handle);
              const product = await read(`products/${handle}.js`);
              if (product?.currency !== undefined && product.currency !== 'EUR') throw failure('currency_changed', 'De winkelvaluta is gewijzigd. Herlaad de pagina en controleer je selectie.');
              if (product?.vendor !== vendor || product?.handle !== handle) throw failure('product_changed', `De gegevens van ${wine.name || handle} zijn gewijzigd. Bekijk deze wijn opnieuw; je selectie blijft bewaard.`, 'unavailable');
              const matches = Array.isArray(product.variants) ? product.variants.filter(variant => variant?.title === 'Fles') : [];
              const variant = matches[0];
              const id = typeof variant?.id === 'number' && Number.isSafeInteger(variant.id) ? String(variant.id) : variant?.id;
              if (matches.length !== 1 || !validId(id) || id !== wine.variantId || !Number.isSafeInteger(variant.price) || variant.price < 0) throw failure('variant_changed', `De flesvariant van ${wine.name || handle} is gewijzigd. Herlaad de pagina of kies een andere wijn; je selectie blijft bewaard.`, 'unavailable');
              return { handle, variantId: id, quantity: amount, price: variant.price, compareAtCents: Number.isSafeInteger(variant.compare_at_price) && variant.compare_at_price >= 0 ? variant.compare_at_price : null, available: variant.available === true };
            })),
          ]),
          aborted,
        ]);
        if (controller.signal.aborted) throw controller.signal.reason;
        sync();
        if (selection() !== base) throw failure('selection_changed', 'Je selectie is in een ander tabblad gewijzigd. Controleer je doos opnieuw.');
        if (cart?.currency !== 'EUR') throw failure('currency_changed', 'De winkelvaluta is gewijzigd. Herlaad de pagina en controleer je selectie.');
        const changed = checked.some(line => line.price !== livePrices[line.handle]);
        for (const line of checked) {
          livePrices[line.handle] = line.price;
          liveCompareAtCents[line.handle] = line.compareAtCents;
          wines.get(line.handle).available = line.available;
        }
        const unavailable = checked.find(line => !line.available);
        if (unavailable) throw failure('unavailable', `${wines.get(unavailable.handle).name || unavailable.handle} is nu niet beschikbaar. Haal deze fles uit je doos en kies een andere wijn.`, 'unavailable');
        if (changed) {
          warn('price_changed', 'Een flesprijs is gewijzigd. Het totaal is bijgewerkt. Controleer je doos en kies opnieuw Verder naar winkelmand.');
          return { status: 'reconfirm', message: notice.message };
        }
        const variants = new Set([...wines.values()].filter(wine => wine.vendor === vendor).map(wine => wine.variantId));
        const updated = await Promise.race([write('cart/update.js', { updates: boxUpdates(cart.items, variants, checked) }), aborted]);
        const inCart = id => (updated?.items || []).filter(item => String(item?.variant_id) === id && !item.selling_plan_allocation).reduce((sum, item) => sum + item.quantity, 0);
        const short = checked.find(line => inCart(line.variantId) !== line.quantity);
        if (short) throw failure('cart_rejected', `Van ${wines.get(short.handle).name || short.handle} kon niet het gekozen aantal in de winkelmand. Controleer je winkelmand; je selectie blijft bewaard.`);
        const url = new URL(`${root}cart`, currentUrl);
        for (const key of ['preview_theme_id', 'preview_key', 'preview', '_fd', 'pb']) {
          if (currentUrl.searchParams.has(key)) url.searchParams.set(key, currentUrl.searchParams.get(key));
        }
        readyBase = base;
        notice = null;
        return { status: 'ready', url: url.href };
      } catch (error) {
        controller.abort(error);
        return finishFailure(error);
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', cancel);
        controller.signal.removeEventListener('abort', onAbort);
        request = null;
        checking = false;
        emit();
      }
    }
    sync();
    eventTarget?.addEventListener('storage', event => {
      if ((event.key === storageKey || event.key === null) && (!event.storageArea || event.storageArea === storage)) {
        sync();
        if (request && selection() !== request.base) request.controller.abort(failure('selection_changed', 'Je selectie is in een ander tabblad gewijzigd. Controleer je doos opnieuw.'));
        emit();
      }
    });
    return {
      getState, add, setQuantity, selectFarmer, acceptProposal, prepareHandoff,
      subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { createMixAdapter, boxUpdates };
  if (typeof window !== 'undefined') {
    const paper = window.DirtPaper ||= {};
    if (paper.createMixAdapter) return;
    paper.createMixAdapter = createMixAdapter;
    const init = () => {
      if (paper.mix || !paper.catalog) return;
      let storage;
      try { storage = window.localStorage; }
      catch { storage = { getItem() { throw new Error('Storage unavailable'); }, setItem() { throw new Error('Storage unavailable'); } }; }
      paper.mix = createMixAdapter({ catalog: paper.catalog, storage, eventTarget: window, fetch: window.fetch.bind(window), location: window.location, root: window.Shopify?.routes?.root || '/' });
    };
    init();
    window.addEventListener('DOMContentLoaded', init);
    window.addEventListener('shopify:section:load', init);
  }
})();
